<template>
  <modal
    v-if="open"
    :title="$t('Inventory.lots_title')"
    closable
    @close-request="requestClose"
  >
    <div class="lots-manager">
      <p class="lots-manager__help">{{ $t('Inventory.lots_help') }}</p>

      <form class="lots-manager__create" @submit.prevent="createLot">
        <label>
          <span>{{ $t('Inventory.lot_name') }} *</span>
          <input
            v-model="newLot.name"
            class="form-control"
            type="text"
            :maxlength="nameMaxLength"
            :disabled="busy"
            required
          >
        </label>
        <label>
          <span>{{ $t('Inventory.lot_description') }}</span>
          <input
            v-model.trim="newLot.description"
            class="form-control"
            type="text"
            :placeholder="$t('Inventory.lot_description_placeholder')"
            :disabled="busy"
          >
        </label>
        <button type="submit" class="btn btn-kne-orange" :disabled="busy || !isValidName(newLot.name)">
          <i :class="['fa', busyAction === 'create' ? 'fa-spinner fa-spin' : 'fa-plus']" aria-hidden="true" />
          {{ busyAction === 'create' ? $t('Inventory.adding_lot') : $t('Inventory.add_lot') }}
        </button>
      </form>

      <div v-if="feedback" class="lots-manager__message" :class="feedbackClass" role="status">
        {{ feedback }}
      </div>

      <div v-if="loading" class="lots-manager__loading">
        <i class="fa fa-spinner fa-spin" aria-hidden="true" />
      </div>

      <div v-else-if="!lots.length" class="lots-manager__empty">
        {{ $t('Inventory.no_lots_configured') }}
      </div>

      <ul v-if="!loading && groups.length" class="lots-manager__list">
        <li v-for="group in groups" :key="group.key" class="lots-manager__lot">
          <form v-if="editingKey === group.key" class="lots-manager__edit" @submit.prevent="saveEdit(group)">
            <label>
              <span>{{ $t('Inventory.lot_name') }} *</span>
              <input
                v-model="editDraft.name"
                class="form-control"
                type="text"
                :maxlength="nameMaxLength"
                :disabled="busy"
                required
              >
            </label>
            <label>
              <span>{{ $t('Inventory.lot_description') }}</span>
              <input
                v-model.trim="editDraft.description"
                class="form-control"
                type="text"
                :placeholder="$t('Inventory.lot_description_placeholder')"
                :disabled="busy"
              >
            </label>
            <div class="lots-manager__edit-actions">
              <button type="button" class="btn btn-light" :disabled="busy" @click="cancelEdit">
                {{ $t('Inventory.cancel') }}
              </button>
              <button type="submit" class="btn btn-kne-orange" :disabled="busy || !isValidName(editDraft.name)">
                <i :class="['fa', busyAction === 'edit' ? 'fa-spinner fa-spin' : 'fa-check']" aria-hidden="true" />
                {{ busyAction === 'edit' ? $t('Inventory.saving_lot') : $t('Inventory.save_lot') }}
              </button>
            </div>
            <small v-if="isRename(group)" class="lots-manager__notice">
              <i class="fa fa-info-circle" aria-hidden="true" />
              {{ $t('Inventory.lot_rename_notice') }}
            </small>
          </form>

          <div v-else class="lots-manager__row">
            <div class="lots-manager__identity" :class="{'is-unassigned': !group.lot}">
              <strong>{{ group.label }}</strong>
              <small v-if="group.lot && group.lot.description">{{ group.lot.description }}</small>
            </div>
            <span class="lots-manager__count">{{ $t('Inventory.lot_vehicle_count', {count: group.orders.length}) }}</span>
            <div class="lots-manager__actions">
              <button
                type="button"
                class="btn btn-light btn-sm"
                :class="{'is-active': expandedKey === group.key}"
                :disabled="busy || !group.orders.length"
                @click="toggleVehicles(group)"
              >
                <i class="fa fa-car" aria-hidden="true" />
                {{ $t('Inventory.lot_vehicles') }}
              </button>
              <template v-if="group.lot">
                <button type="button" class="btn btn-light btn-sm" :disabled="busy" @click="startEdit(group)">
                  <i class="fa fa-pencil" aria-hidden="true" />
                  {{ $t('Inventory.edit_lot') }}
                </button>
                <button
                  type="button"
                  class="btn btn-light btn-sm lots-manager__delete"
                  :disabled="busy || Boolean(deleteBlockedReason(group))"
                  :title="deleteBlockedReason(group)"
                  @click="confirmDeleteKey = group.key"
                >
                  <i class="fa fa-trash" aria-hidden="true" />
                  {{ $t('Inventory.delete_lot') }}
                </button>
              </template>
            </div>
          </div>

          <div v-if="confirmDeleteKey === group.key" class="lots-manager__confirm" role="alert">
            <span>{{ $t('Inventory.confirm_delete_lot', {lot: group.label}) }}</span>
            <div>
              <button type="button" class="btn btn-light btn-sm" :disabled="busy" @click="confirmDeleteKey = ''">
                {{ $t('Inventory.cancel') }}
              </button>
              <button type="button" class="btn btn-danger btn-sm" :disabled="busy" @click="deleteLot(group)">
                <i :class="['fa', busyAction === 'delete' ? 'fa-spinner fa-spin' : 'fa-trash']" aria-hidden="true" />
                {{ $t('Inventory.delete_lot') }}
              </button>
            </div>
          </div>

          <div v-if="expandedKey === group.key" class="lots-manager__vehicles">
            <p v-if="!group.orders.length" class="lots-manager__empty">{{ $t('Inventory.lot_empty_vehicles') }}</p>
            <template v-else>
              <label class="lots-manager__vehicle lots-manager__vehicle--all">
                <input
                  type="checkbox"
                  :checked="allSelected(group)"
                  :disabled="busy"
                  @change="toggleAll(group, $event.target.checked)"
                >
                <span>{{ $t('Inventory.lot_select_all_vehicles') }}</span>
              </label>
              <label v-for="order in group.orders" :key="orderId(order)" class="lots-manager__vehicle">
                <input v-model="selectedOrderIds" type="checkbox" :value="orderId(order)" :disabled="busy">
                <strong>{{ orderId(order) }}</strong>
                <span>{{ vehicleTitle(order) }}</span>
                <code>{{ order.vin || '' }}</code>
              </label>

              <div class="lots-manager__transfer">
                <label>
                  <span>{{ $t('Inventory.transfer_to_lot') }}</span>
                  <select v-model="transferTarget" class="form-control" :disabled="busy">
                    <option value="">{{ $t('Inventory.choose_lot') }}</option>
                    <option v-for="name in transferTargets(group)" :key="name" :value="name">{{ name }}</option>
                  </select>
                </label>
                <button
                  type="button"
                  class="btn btn-kne-orange"
                  :disabled="busy || !selectedOrderIds.length || !transferTarget"
                  @click="transfer"
                >
                  <i :class="['fa', busyAction === 'transfer' ? 'fa-spinner fa-spin' : 'fa-exchange']" aria-hidden="true" />
                  {{ busyAction === 'transfer'
                    ? $t('Inventory.transferring_vehicles')
                    : $t('Inventory.transfer_vehicles', {count: selectedOrderIds.length}) }}
                </button>
              </div>
            </template>
          </div>
        </li>
      </ul>

      <footer>
        <button type="button" class="btn btn-light" :disabled="busy" @click="requestClose">
          {{ $t('Inventory.close') }}
        </button>
      </footer>
    </div>
  </modal>
</template>

<script>
import Modal from '../../interface-components/Modal.vue'
import {
  INVENTORY_LOT_NAME_MAX_LENGTH,
  isValidInventoryLotName,
  normalizeInventoryLotName
} from '../../../../inventory/inventorySelectors'
import {
  createInventoryLot,
  deleteInventoryLot,
  transferInventoryLotOrders,
  updateInventoryLotDetails
} from '../../../../inventory/inventoryService'

const UNASSIGNED_GROUP = '__unassigned__'
const STALE_LOT_ERRORS = ['INVENTORY_LOT_NOT_FOUND', 'INVALID_INVENTORY_LOT']

export default {
  name: 'InventoryLotsModal',

  components: {
    Modal
  },

  props: {
    open: {
      type: Boolean,
      default: false
    },
    orders: {
      type: Array,
      default: () => []
    }
  },

  data () {
    return {
      nameMaxLength: INVENTORY_LOT_NAME_MAX_LENGTH,
      newLot: {name: '', description: ''},
      editingKey: '',
      editDraft: {name: '', description: ''},
      confirmDeleteKey: '',
      expandedKey: '',
      selectedOrderIds: [],
      transferTarget: '',
      busyAction: '',
      loading: false,
      feedback: '',
      feedbackType: 'success'
    }
  },

  computed: {
    busy () {
      return Boolean(this.busyAction)
    },
    companyUID () {
      return this.$store.state.authorization && this.$store.state.authorization.company
    },
    operator () {
      const user = this.$store.state.user || {}
      const userInfo = this.$store.state.userInfo || {}
      return {
        uid: user.uid || '',
        name: userInfo.name || user.displayName || user.email || ''
      }
    },
    lots () {
      return this.$store.state.inventoryLots
    },
    lotNames () {
      return this.$store.getters.inventoryLotNames
    },
    groups () {
      const byLot = {}
      const unassigned = []
      this.orders.forEach(order => {
        const lot = normalizeInventoryLotName(order.inventoryLot)
        if (this.lotNames.includes(lot)) {
          if (!byLot[lot]) byLot[lot] = []
          byLot[lot].push(order)
        } else {
          unassigned.push(order)
        }
      })

      const groups = this.lots.map(lot => ({
        key: lot.id,
        lot,
        label: lot.name,
        orders: byLot[lot.name] || []
      }))
      if (unassigned.length) {
        groups.push({key: UNASSIGNED_GROUP, lot: null, label: this.$t('Inventory.unassigned_lot'), orders: unassigned})
      }
      return groups
    },
    feedbackClass () {
      return this.feedbackType === 'error' ? 'lots-manager__message--error' : 'lots-manager__message--success'
    }
  },

  watch: {
    open (value) {
      if (value) this.reset()
    },
    groups () {
      const group = this.groups.find(item => item.key === this.expandedKey)
      if (!group) {
        this.expandedKey = ''
        this.selectedOrderIds = []
        return
      }
      const ids = group.orders.map(this.orderId)
      this.selectedOrderIds = this.selectedOrderIds.filter(id => ids.includes(id))
    }
  },

  created () {
    if (this.open) this.reset()
  },

  methods: {
    async reset () {
      this.newLot = {name: '', description: ''}
      this.editingKey = ''
      this.confirmDeleteKey = ''
      this.expandedKey = ''
      this.selectedOrderIds = []
      this.transferTarget = ''
      this.feedback = ''
      this.loading = true
      await this.$store.dispatch('loadInventoryLots', {force: true})
      this.loading = false
    },
    orderId (order) {
      return String(order['.key'] || order.orderNumber || order.key || '')
    },
    vehicleTitle (order) {
      return [order.year, order.make, order.model, order.trim].filter(Boolean).join(' ') ||
        this.$t('Inventory.vehicle_pending')
    },
    isValidName (value) {
      return isValidInventoryLotName(value)
    },
    isRename (group) {
      return Boolean(group.lot) && normalizeInventoryLotName(this.editDraft.name) !== group.lot.name
    },
    deleteBlockedReason (group) {
      if (group.orders.length) return this.$t('Inventory.lot_delete_in_use')
      if (this.lots.length <= 1) return this.$t('Inventory.lot_delete_last')
      return ''
    },
    transferTargets (group) {
      return this.lotNames.filter(name => !group.lot || name !== group.lot.name)
    },
    allSelected (group) {
      return group.orders.length > 0 && group.orders.every(order => this.selectedOrderIds.includes(this.orderId(order)))
    },
    toggleAll (group, checked) {
      this.selectedOrderIds = checked ? group.orders.map(this.orderId) : []
    },
    toggleVehicles (group) {
      this.expandedKey = this.expandedKey === group.key ? '' : group.key
      this.selectedOrderIds = []
      this.transferTarget = ''
    },
    startEdit (group) {
      this.editingKey = group.key
      this.confirmDeleteKey = ''
      this.editDraft = {name: group.lot.name, description: group.lot.description}
    },
    cancelEdit () {
      this.editingKey = ''
    },
    errorMessage (error) {
      const code = error && error.code
      if (code === 'INVENTORY_LOT_EXISTS') return this.$t('Inventory.lot_exists_error')
      if (code === 'INVALID_INVENTORY_LOT_NAME') return this.$t('Inventory.lot_name_invalid', {max: this.nameMaxLength})
      if (code === 'INVENTORY_LOT_IN_USE') return this.$t('Inventory.lot_in_use_error')
      if (STALE_LOT_ERRORS.includes(code)) return this.$t('Inventory.lot_list_changed_error')
      return `${this.$t('Inventory.lot_action_error')}${code ? ` (${code})` : ''}`
    },
    async run (action, operation) {
      if (this.busy || !this.companyUID) return
      this.busyAction = action
      this.feedback = ''
      try {
        const message = await operation()
        await this.$store.dispatch('loadInventoryLots', {force: true})
        this.feedback = message
        this.feedbackType = 'success'
      } catch (error) {
        if (STALE_LOT_ERRORS.includes(error && error.code)) {
          await this.$store.dispatch('loadInventoryLots', {force: true})
        }
        this.feedback = this.errorMessage(error)
        this.feedbackType = 'error'
      } finally {
        this.busyAction = ''
      }
    },
    createLot () {
      return this.run('create', async () => {
        const {lot} = await createInventoryLot({
          companyUID: this.companyUID,
          name: this.newLot.name,
          description: this.newLot.description,
          operator: this.operator
        })
        this.newLot = {name: '', description: ''}
        return this.$t('Inventory.lot_created_success', {lot: lot.name})
      })
    },
    saveEdit (group) {
      return this.run('edit', async () => {
        const result = await updateInventoryLotDetails({
          companyUID: this.companyUID,
          lotName: group.lot.name,
          name: this.editDraft.name,
          description: this.editDraft.description,
          operator: this.operator
        })
        this.editingKey = ''
        return result.previousName === result.lot.name
          ? this.$t('Inventory.lot_saved_success', {lot: result.lot.name})
          : this.$t('Inventory.lot_renamed_success', {
            previous: result.previousName,
            lot: result.lot.name,
            count: result.renamedOrderIds.length
          })
      })
    },
    deleteLot (group) {
      return this.run('delete', async () => {
        await deleteInventoryLot({
          companyUID: this.companyUID,
          lotName: group.lot.name,
          operator: this.operator
        })
        this.confirmDeleteKey = ''
        return this.$t('Inventory.lot_deleted_success', {lot: group.lot.name})
      })
    },
    transfer () {
      const orderIds = this.selectedOrderIds.slice()
      const inventoryLot = this.transferTarget
      return this.run('transfer', async () => {
        const result = await transferInventoryLotOrders({
          companyUID: this.companyUID,
          orderIds,
          inventoryLot,
          operator: this.operator
        })
        this.selectedOrderIds = []
        return this.$t('Inventory.lot_transfer_success', {count: result.movedOrderIds.length, lot: result.inventoryLot})
      })
    },
    requestClose () {
      if (!this.busy) this.$emit('close')
    }
  }
}
</script>

<style scoped>
.lots-manager {
  width: min(780px, 92vw);
  max-width: 100%;
  padding: 20px 24px 24px;
  color: #344250;
}

.lots-manager__help {
  margin-bottom: 16px;
  color: #6e7c89;
  font-size: .9rem;
}

.lots-manager label > span {
  display: block;
  margin-bottom: 3px;
  color: #7b8793;
  font-size: .73rem;
  font-weight: 700;
}

.lots-manager__create,
.lots-manager__edit {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 2fr) auto;
  gap: 10px;
  align-items: end;
}

.lots-manager__create {
  padding: 14px;
  margin-bottom: 14px;
  border-radius: 10px;
  background: #f4f6f8;
}

.lots-manager__create label,
.lots-manager__edit label {
  margin: 0;
}

.lots-manager__edit-actions {
  display: flex;
  gap: 8px;
}

.lots-manager__notice {
  grid-column: 1 / -1;
  color: #a5671a;
}

.lots-manager__message {
  padding: 10px 12px;
  margin-bottom: 12px;
  border-radius: 8px;
}

.lots-manager__message--success {
  color: #23633c;
  background: #e3f5ea;
}

.lots-manager__message--error {
  color: #8d2f32;
  background: #fde7e7;
}

.lots-manager__loading,
.lots-manager__empty {
  padding: 18px;
  color: #7b8793;
  text-align: center;
}

.lots-manager__list {
  max-height: 55vh;
  padding: 0;
  margin: 0;
  overflow-y: auto;
  list-style: none;
}

.lots-manager__lot {
  padding: 10px 0;
  border-bottom: 1px solid #e6eaee;
}

.lots-manager__row {
  display: flex;
  gap: 12px;
  align-items: center;
}

.lots-manager__identity {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;
}

.lots-manager__identity strong {
  color: #283644;
}

.lots-manager__identity.is-unassigned strong {
  color: #a5671a;
}

.lots-manager__identity small {
  overflow: hidden;
  color: #7b8793;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lots-manager__count {
  color: #6e7c89;
  font-size: .82rem;
  white-space: nowrap;
}

.lots-manager__actions {
  display: flex;
  gap: 6px;
}

.lots-manager__actions .is-active {
  border-color: #f08a24;
  color: #c76a0f;
}

.lots-manager__delete:not(:disabled) {
  color: #b53b3f;
}

.lots-manager__confirm {
  display: flex;
  gap: 10px;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  margin-top: 8px;
  border-radius: 8px;
  color: #8d2f32;
  background: #fde7e7;
}

.lots-manager__confirm div {
  display: flex;
  gap: 6px;
}

.lots-manager__vehicles {
  padding: 10px 12px;
  margin-top: 8px;
  border-radius: 8px;
  background: #f4f6f8;
}

.lots-manager__vehicle {
  display: grid;
  grid-template-columns: auto auto minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  padding: 4px 0;
  margin: 0;
  cursor: pointer;
}

.lots-manager__vehicle span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.lots-manager__vehicle--all {
  grid-template-columns: auto 1fr;
  padding-bottom: 6px;
  border-bottom: 1px solid #e0e5ea;
  font-weight: 700;
}

.lots-manager__transfer {
  display: flex;
  gap: 10px;
  align-items: flex-end;
  padding-top: 10px;
  margin-top: 6px;
  border-top: 1px solid #e0e5ea;
}

.lots-manager__transfer label {
  flex: 1;
  margin: 0;
}

.lots-manager footer {
  display: flex;
  justify-content: flex-end;
  padding-top: 16px;
  margin-top: 8px;
}

@media (max-width: 600px) {
  .lots-manager {
    width: 100%;
    padding: 16px;
  }

  .lots-manager__create,
  .lots-manager__edit {
    grid-template-columns: 1fr;
  }

  .lots-manager__row {
    flex-wrap: wrap;
  }

  .lots-manager__actions {
    width: 100%;
  }

  .lots-manager__vehicle {
    grid-template-columns: auto auto minmax(0, 1fr);
  }

  .lots-manager__vehicle code {
    display: none;
  }

  .lots-manager__transfer {
    flex-direction: column;
    align-items: stretch;
  }
}
</style>
